import { useEffect, useState } from "react";
import { Check, Infinity as InfinityIcon } from "lucide-react";

const CATEGORIES = {
  models: {
    label: "AI Vision Models",
    defaultTier: "lifetime",
    toggle: [
      { key: "monthly", label: "Monthly (€30)" },
      { key: "lifetime", label: "Lifetime (€165)" },
    ],
    cards: [
      {
        key: "monthly",
        tag: "MONTHLY SUBSCRIPTION",
        name: "Monthly License",
        price: "€30",
        unit: "/ month",
        features: [
          "All FPS Title AI Models",
          "Regular Weight Retraining",
          "Standard Support Ticket Access",
        ],
        cta: "Purchase Monthly",
        badge: null,
      },
      {
        key: "lifetime",
        tag: "PERMANENT ACCESS",
        name: "Lifetime License",
        price: "€165",
        unit: "one-time",
        features: [
          "Lifetime Unlimited Downloads",
          "Future Game Expansion Packs",
          "VIP Priority Discord Role",
          "Priority Developer Support",
        ],
        cta: "Get Lifetime Access",
        badge: "BEST VALUE",
      },
    ],
  },
  woofer: {
    label: "Woofer",
    defaultTier: "woofer_lifetime",
    toggle: [
      { key: "woofer_once", label: "One-Time (€25)" },
      { key: "woofer_lifetime", label: "Lifetime (€60)" },
    ],
    cards: [
      {
        key: "woofer_once",
        tag: "SINGLE PURCHASE",
        name: "Woofer One-Time",
        price: "€25",
        unit: "one-time",
        features: [
          "Full Woofer Access",
          "Digital Delivery Via Discord",
          "Standard Support Access",
        ],
        cta: "Get Woofer",
        badge: null,
      },
      {
        key: "woofer_lifetime",
        tag: "PERMANENT ACCESS",
        name: "Woofer Lifetime",
        price: "€60",
        unit: "one-time",
        features: [
          "Lifetime Woofer Access",
          "All Future Woofer Updates",
          "Priority Discord Support",
        ],
        cta: "Get Woofer Lifetime",
        badge: "BEST VALUE",
      },
    ],
  },
};

const Pricing = ({ onSelectPlan }) => {
  const [category, setCategory] = useState("models");
  const [tier, setTier] = useState(CATEGORIES.models.defaultTier);

  const cat = CATEGORIES[category];

  const switchCategory = (key) => {
    setCategory(key);
    setTier(CATEGORIES[key].defaultTier);
  };

  useEffect(() => {
    const handler = () => switchCategory("woofer");
    window.addEventListener("scythe:show-woofer", handler);
    return () => window.removeEventListener("scythe:show-woofer", handler);
  }, []);

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 px-6 border-b border-red-500/20">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-xl mx-auto mb-12">
          <p className="font-mono-x text-xs text-[#ff4655] uppercase tracking-widest mb-1">Membership Tiers</p>
          <h2 className="font-display text-5xl uppercase text-white mb-6">Choose Your Access</h2>

          <div className="inline-flex items-center gap-2 p-1 bg-zinc-900 border border-zinc-700 mb-4" data-testid="category-tabs">
            {Object.entries(CATEGORIES).map(([key, c]) => (
              <button
                key={key}
                type="button"
                data-testid={`category-tab-${key}`}
                onClick={() => switchCategory(key)}
                className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  category === key ? "bg-white text-black" : "text-zinc-300 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center gap-2 p-1 bg-zinc-900 border border-zinc-700" data-testid="tier-toggle">
            {cat.toggle.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`tier-toggle-${t.key.replace(/_/g, "-")}`}
                onClick={() => setTier(t.key)}
                className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                  tier === t.key ? "bg-[#ff4655] text-white" : "text-zinc-300 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {cat.cards.map((card) => {
            const active = tier === card.key;
            return (
              <div
                key={card.key}
                data-testid={`pricing-card-${card.key.replace(/_/g, "-")}`}
                className={`p-8 flex flex-col justify-between relative transition-colors ${
                  active
                    ? "bg-[#180e12] border-2 border-[#ff4655] shadow-[0_0_30px_rgba(255,70,85,0.2)]"
                    : "bg-zinc-900/80 border border-zinc-800"
                }`}
              >
                {card.badge && (
                  <div className="absolute -top-3 right-6 px-3 py-1 bg-[#ff4655] text-white text-[10px] font-black uppercase flex items-center gap-1">
                    <InfinityIcon className="w-3 h-3" /> {card.badge}
                  </div>
                )}
                <div>
                  <span className={`font-mono-x text-xs uppercase ${active ? "text-[#ff4655]" : "text-zinc-500"}`}>
                    {card.tag}
                  </span>
                  <h3 className="font-display text-3xl uppercase text-white mt-1 mb-4">{card.name}</h3>
                  <div className="mb-6">
                    <span className="font-display text-6xl text-white">{card.price}</span>
                    <span className="text-zinc-400 text-xs font-mono-x"> {card.unit}</span>
                  </div>
                  <ul className="space-y-3 text-xs text-zinc-300 mb-8">
                    {card.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-[#ff4655] shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  type="button"
                  data-testid={`pricing-${card.key.replace(/_/g, "-")}-btn`}
                  onClick={() => onSelectPlan(card.key)}
                  className={`w-full text-center py-3 font-bold text-xs uppercase tracking-wider transition-colors ${
                    card.badge
                      ? "bg-[#ff4655] hover:bg-white hover:text-black text-white font-extrabold"
                      : "bg-zinc-800 hover:bg-[#ff4655] text-white"
                  }`}
                >
                  {card.cta}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-zinc-500 text-xs font-mono-x mt-10 max-w-2xl mx-auto">
          Digital software only. No physical goods. After checkout, verify your receipt in Discord to receive your files.
        </p>
      </div>
    </section>
  );
};

export default Pricing;
